import type { FC, ReactNode } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { OnboardingResponsiveCtaFooter } from "../shared/OnboardingResponsiveCtaFooter";
import { ONBOARDING_FLOATING_CTA_CONTENT_RESERVE_CLASS } from "../shared/onboardingFloatingCtaTokens";

interface QuizProgressHeaderProps {
  progressIndex: number;
  progressTotal: number;
  onBack?: () => void;
  showProgress?: boolean;
  variant?: "dark" | "light";
}

export const QuizProgressHeader: FC<QuizProgressHeaderProps> = ({
  progressIndex,
  progressTotal,
  onBack,
  showProgress = true,
  variant = "dark",
}) => {
  const progressPercent =
    progressTotal > 0 ? (progressIndex / progressTotal) * 100 : 0;

  return (
    <header
      className="flex shrink-0 items-center gap-3 pb-3 pt-0"
      data-testid="quiz-progress-header"
    >
      {onBack ? (
        <button
          type="button"
          aria-label="Go back"
          data-testid="quiz-back-button"
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-full shadow-[0_0_16px_rgba(0,0,0,0.08)]",
            variant === "dark"
              ? "bg-transparent"
              : "bg-background_solid_neutral_primary",
          )}
          onClick={onBack}
        >
          <Icon
            name="left-arrow-alt"
            size={20}
            className={
              variant === "dark"
                ? "text-content_inverse_primary"
                : "text-content_primary"
            }
          />
        </button>
      ) : (
        <span className="size-10 shrink-0" />
      )}

      {showProgress ? (
        <>
          <div
            className={cn(
              "h-1.5 min-w-0 flex-1 overflow-hidden rounded",
              variant === "dark"
                ? "bg-border_white_transparent10"
                : "bg-border_aura-lv3_solid",
            )}
          >
            <div
              className="h-full rounded-[10px] bg-content_aura_lv3_primary transition-[width] duration-300"
              style={{ width: `${progressPercent}%` }}
              data-testid="quiz-progress-bar"
            />
          </div>
          <span
            className={cn(
              "body_medium_m w-[30px] shrink-0 text-right",
              variant === "dark"
                ? "text-content_inverse_primary"
                : "text-content_primary",
            )}
            data-testid="quiz-progress-label"
          >
            {progressIndex} / {progressTotal}
          </span>
        </>
      ) : (
        <span className="flex-1" />
      )}
    </header>
  );
};

interface QuizStepLayoutProps {
  variant?: "dark" | "light";
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  testId?: string;
  /** H5 底部为浮动 CTA 预留滚动空间；无 footer 的步骤应设为 false */
  reserveFloatingCtaSpace?: boolean;
}

export const QuizStepLayout: FC<QuizStepLayoutProps> = ({
  variant = "dark",
  header,
  footer,
  children,
  className,
  testId,
  reserveFloatingCtaSpace = true,
}) => (
  <div
    data-testid={testId}
    data-layout="quiz-step-responsive"
    className={cn(
      "relative flex min-h-dvh w-full flex-col overflow-hidden pt-safe md:overflow-y-auto",
      variant === "dark"
        ? "bg-content_brand_primary text-content_inverse_primary"
        : "bg-background_solid_neutral_secondary text-content_primary",
      className,
    )}
  >
    {header ? (
      <div
        className="relative z-[1] w-full shrink-0 pt-2 md:pt-16"
        data-testid="quiz-pc-header-shell"
      >
        <div
          className="mx-auto w-full px-4 md:max-w-[740px] md:px-4"
          data-testid="quiz-pc-header-band"
        >
          {header}
        </div>
      </div>
    ) : null}
    <div
      className={cn(
        // H5: flex-1 scroll under floating CTA; PC: natural height + in-flow footer margin (134px)
        "relative z-[1] flex max-md:min-h-0 max-md:flex-1 flex-col px-4 md:mx-auto md:w-full md:max-w-[460px] md:shrink-0 md:px-0 md:pt-12 max-md:overflow-y-auto md:overflow-visible no-scrollbar",
        reserveFloatingCtaSpace &&
          ONBOARDING_FLOATING_CTA_CONTENT_RESERVE_CLASS,
      )}
      data-testid="quiz-pc-content-column"
    >
      {children}
    </div>
    {footer ? (
      <OnboardingResponsiveCtaFooter
        inset="narrow"
        testId="quiz-pc-footer-band"
      >
        {footer}
      </OnboardingResponsiveCtaFooter>
    ) : null}
  </div>
);
